
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

export interface MetaConnectionStatus {
  isConnected: boolean;
  lastChecked: string | null;
  connectionError: string | null;
  adAccountId: string | null;
  lastSynced: string | null;
  isLoading: boolean;
}

export const useMetaConnection = () => {
  const [connectionStatus, setConnectionStatus] = useState<MetaConnectionStatus>({
    isConnected: false,
    lastChecked: null,
    connectionError: null,
    adAccountId: null,
    lastSynced: null,
    isLoading: true
  });
  const [isSyncing, setIsSyncing] = useState(false);
  const { user } = useAuth();
  const { toast: uiToast } = useToast();

  // Fetch connection status from Supabase
  const fetchConnectionStatus = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      console.log("Checking Meta connection status for user:", user.id);
      setConnectionStatus(prev => ({ ...prev, isLoading: true }));
      
      // Check if connection exists for this user
      const { data, error } = await supabase
        .from('meta_connections')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (error) {
        console.error('Failed to fetch Meta connection status:', error.message);
        setConnectionStatus(prev => ({ 
          ...prev, 
          isLoading: false,
          connectionError: `API Error: ${error.message}`
        }));
        return;
      }
      
      if (data) {
        console.log("Meta connection data:", data);
        setConnectionStatus({
          isConnected: data.is_connected || false,
          lastChecked: data.last_checked,
          connectionError: data.connection_error,
          adAccountId: data.ad_account_id,
          lastSynced: data.last_synced,
          isLoading: false
        });
      } else {
        console.log("No connection data found");
        setConnectionStatus(prev => ({ 
          ...prev, 
          isLoading: false,
          isConnected: false
        }));
      }
    } catch (error) {
      console.error('Error checking Meta connection:', error);
      setConnectionStatus(prev => ({ 
        ...prev, 
        isLoading: false,
        connectionError: error instanceof Error ? error.message : 'Unbekannter Fehler'
      }));
    }
  }, [user?.id]);

  // Central method to reconnect to Meta (for admin use)
  const reconnectToMeta = useCallback(async () => {
    toast.info("Initiating Meta connection setup through admin panel...");
    // This function will be used by admins to initiate connection process
    // It can open a dialog or redirect to a setup page
    try {
      // For now, just request the connection setup
      requestConnectionSetup();
    } catch (error) {
      console.error("Error reconnecting to Meta:", error);
      toast.error("Fehler bei der Meta-Verbindung");
    }
  }, []);

  // Method to sync Meta insights data
  const syncMetaInsights = useCallback(async () => {
    if (!user?.id) {
      toast.error("Benutzer nicht angemeldet");
      return;
    }
    
    try {
      setIsSyncing(true);
      
      // Call the edge function to sync insights
      const { error } = await supabase.functions.invoke('sync-meta-insights', {
        body: { user_id: user.id }
      });
      
      if (error) {
        throw new Error(error.message);
      }
      
      toast.success("Meta-Daten werden synchronisiert");
      
      // Update last synced timestamp
      setTimeout(() => {
        fetchConnectionStatus();
      }, 2000);
      
    } catch (error) {
      console.error("Error syncing Meta insights:", error);
      toast.error("Fehler bei der Synchronisierung der Meta-Daten");
    } finally {
      setIsSyncing(false);
    }
  }, [user?.id, fetchConnectionStatus]);

  // Simplified method to call admin to set up the connection
  const requestConnectionSetup = useCallback(() => {
    toast.info("Das Admin-Team wurde informiert. Sie werden kontaktiert, um die Meta-Verbindung einzurichten.");
  }, []);

  // Initialize connection status check
  useEffect(() => {
    if (user?.id) {
      console.log("Initializing Meta connection check for user:", user.id);
      fetchConnectionStatus();
      
      // Set up periodic refresh
      const intervalId = setInterval(fetchConnectionStatus, 5 * 60 * 1000); // Check every 5 minutes
      
      return () => clearInterval(intervalId);
    } else {
      setConnectionStatus(prev => ({ ...prev, isLoading: false }));
    }
  }, [user, fetchConnectionStatus]);

  return {
    ...connectionStatus,
    isSyncing,
    requestConnectionSetup,
    refreshConnectionStatus: fetchConnectionStatus,
    syncMetaInsights, // Add the new method
    reconnectToMeta   // Add the new method
  };
};
